/**
 * Single source of truth for the 17-slot pastel palette used by every
 * "color chip" surface (assignee, 領域, future labels). Centralised here
 * so that:
 *
 *   1. The same `colorIndex` resolves to the same Tailwind classes
 *      regardless of which chip rendered it.
 *   2. PurgeCSS sees every class as a string literal in one file.
 *   3. Adding/removing a slot only requires bumping
 *      `ASSIGNEE_COLOR_SLOTS` in shared and editing this one array.
 *
 * The 領域 (area) editor and the assignee chip both call into the
 * helpers here; `assigneeColor.ts` and `areaColor.ts` are now thin
 * façades that exist only for clearer call-site naming.
 */
import { ASSIGNEE_COLOR_SLOTS } from '@mct/shared';

/**
 * Palette index space is shared with `ASSIGNEE_COLOR_SLOTS`. Both
 * assignee chips and area chips draw colors out of this single array,
 * so a "colorIndex 7" rendered next to an assignee with the same index
 * looks identical.
 */
/**
 * 10 visually-distinct slots. Slot 0 is a saturated gray so users have
 * an explicit "neutral" pick that is still a positive choice (distinct
 * from `UNASSIGNED_CLASSES` below, which is reserved for the null /
 * unselected state). The remaining 9 hues are spread around the wheel
 * with enough lightness/chroma separation that adjacent slots in the
 * picker don't read as the same color.
 *
 * If you change this list, also bump `ASSIGNEE_COLOR_SLOTS` in
 * `@mct/shared` — the runtime guard below catches drift at module load.
 */
export const PALETTE: readonly string[] = [
  'bg-slate-200 text-slate-800',
  'bg-red-100 text-red-800',
  'bg-orange-100 text-orange-800',
  'bg-yellow-100 text-yellow-800',
  'bg-emerald-100 text-emerald-800',
  'bg-teal-100 text-teal-800',
  'bg-blue-100 text-blue-800',
  'bg-indigo-100 text-indigo-800',
  'bg-violet-100 text-violet-800',
  'bg-pink-100 text-pink-800',
];

if (PALETTE.length !== ASSIGNEE_COLOR_SLOTS) {
  // Fail-fast at module load; mismatched modulus would silently render
  // the wrong color for a doc whose `colorIndex` was created on the
  // other side of the boundary.
  throw new Error(
    `colorPalette PALETTE length (${PALETTE.length}) must equal ASSIGNEE_COLOR_SLOTS (${ASSIGNEE_COLOR_SLOTS})`,
  );
}

/** Number of slots in the palette. Mirror of `ASSIGNEE_COLOR_SLOTS`. */
export const PALETTE_SIZE: number = PALETTE.length;

/** Slate fallback for "unset" / null inputs (assignee or area). */
export const UNASSIGNED_CLASSES = 'bg-slate-100 text-slate-500';

/**
 * Resolve palette classes from a stored `colorIndex`. Out-of-range
 * indices (corrupt doc / future palette mismatch) wrap with modulus so
 * the chip never blanks out.
 */
export function colorClassesByIndex(
  colorIndex: number | null | undefined,
): string {
  if (colorIndex == null || !Number.isFinite(colorIndex)) {
    return UNASSIGNED_CLASSES;
  }
  const wrapped =
    ((Math.trunc(colorIndex) % PALETTE.length) + PALETTE.length) % PALETTE.length;
  return PALETTE[wrapped]!;
}

/**
 * Deterministic FNV-31a-style hash → palette index. Mirrors
 * `assigneeColorIndex` from `@mct/shared` (same algorithm) so a free-text
 * area or assignee whose only identity is a name hashes to the same
 * slot here. Useful as a fallback when no explicit `colorIndex` is set.
 */
export function colorIndexFromName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h * 31) + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE.length;
}

/**
 * Pick the lowest palette slot not already used by `taken`. Wraps to 0
 * when every slot is in use (multi-occupancy is allowed; the user can
 * always re-pick). Used by the area editor to auto-assign a color when
 * a new entry is added.
 *
 * The "lowest unused first" ordering is deliberate: it ships visibly
 * different colors for the first N adds, while still being deterministic
 * so consecutive adds in a fresh project produce a stable rainbow.
 */
export function pickNextFreeColorIndex(
  taken: Iterable<number | null | undefined>,
): number {
  const used = new Set<number>();
  for (const v of taken) {
    if (v == null || !Number.isFinite(v)) continue;
    used.add(
      ((Math.trunc(v) % PALETTE.length) + PALETTE.length) % PALETTE.length,
    );
  }
  for (let i = 0; i < PALETTE.length; i++) {
    if (!used.has(i)) return i;
  }
  // All slots are taken — wrap around. Caller can still override.
  return 0;
}
